import { BlogCard } from "@/components/blog/BlogCard";
import { BlogNewsletterSignupSection } from "@/components/blog/BlogNewsletterSignupSection";
import { Footer } from "@/components/layout/Footer";
import { GlobalHeroBackground } from "@/components/layout/GlobalHeroBackground";
import { Navbar } from "@/components/layout/Navbar";
import { useSEO } from "@/hooks/useSEO";
import { absoluteUrl } from "@/lib/site";
import { useBlogList, useBlogPost } from "@/hooks/useBlogs";
import { getBlogPostContent, getRelatedBlogPosts, mapDbBlogToPost } from "@/lib/blogs";
import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";
import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: dbPost, loading } = useBlogPost(slug);
  const { data: dbBlogs } = useBlogList();

  const post = useMemo(
    () => (dbPost ? mapDbBlogToPost(dbPost) : null),
    [dbPost],
  );

  const allPosts = useMemo(
    () => (dbBlogs ?? []).map(mapDbBlogToPost),
    [dbBlogs],
  );

  const jsonLd = useMemo(() => {
    if (!post) return undefined;
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.heroImage,
      "datePublished": post.publishedAt,
      "author": {
        "@type": "Person",
        "name": post.author.name,
      },
      "publisher": {
        "@type": "Organization",
        "name": "BollyRhythm",
        "logo": {
          "@type": "ImageObject",
          "url": absoluteUrl("/logo-dark.png"),
        },
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": absoluteUrl(`/blog-post/${post.slug}`),
      },
    } as Record<string, unknown>;
  }, [post]);

  useSEO({
    title: post?.title ?? "Blog Post",
    description: post?.excerpt,
    canonical: post ? `/blog-post/${post.slug}` : undefined,
    ogTitle: post?.title,
    ogDescription: post?.excerpt,
    ogImage: post?.heroImage,
    ogType: "article",
    jsonLd,
  });

  const content = post ? getBlogPostContent(post) : null;
  const relatedPosts = post ? getRelatedBlogPosts(allPosts, post.slug) : [];
  const tocItemClassName = (level: number) =>
    cn(
      "text-left text-[#767474] transition-colors hover:text-pink-dark",
      level === 2 && "pl-3",
      level >= 3 && "pl-6",
    );

  if (loading) {
    return (
      <div className="page-wrapper landing-page relative">
        <Navbar />
        <GlobalHeroBackground />
        <main className="landing-page__main main-wrapper">
          <div className="flex justify-center py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-dark border-t-transparent" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/backstage-talk" replace />;
  }

  return (
    <div className="page-wrapper landing-page relative">
      <Navbar />
      <GlobalHeroBackground />

      <main className="landing-page__main main-wrapper">
        <section>
          <div className="padding-global">
            <div className="container-large">
              <div className="padding-section-large">
                <div className="text-align-center">
                  <div className="text-[14px] font-semibold tracking-[0.075em] text-pink-dark uppercase">
                    {post.category}
                  </div>
                  <div className="spacer-small" />
                  <h1 className="heading-style-h2 m-0">{post.title}</h1>
                  <div className="spacer-small" />
                  <p className="align-center mx-auto max-w-[39ch]">{post.publishedAt}</p>

                  <div className="relative mt-12 aspect-[2.39] overflow-hidden rounded-[var(--radius-large)]">
                    <img src={post.heroImage} alt="" className="absolute inset-0 h-full w-full object-cover object-[50%_0%]" />
                  </div>

                  <div className="mx-auto mt-8 flex items-center justify-center gap-4 text-left max-md:items-start max-[479px]:flex-col max-[479px]:items-center">
                    <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded">
                      <img src={post.author.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
                    </div>
                    <div>
                      <div className="text-[10px] text-neutral-light uppercase">Author</div>
                      <div className="text-[14px]">{post.author.name}</div>
                      <div className="spacer-xsmall" />
                      <p className="max-w-[32rem] text-[14px]">{post.author.bio}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="padding-global">
            <div className="container-large">
              <div className="flex flex-col items-start gap-10 lg:flex-row">
                <aside className="w-full rounded-[var(--radius-main)] bg-white p-6 max-[479px]:p-4 lg:sticky lg:top-16 lg:max-w-[17.5rem] lg:shrink-0">
                  <div className="mb-3 font-display text-[1.5rem] leading-[1.2]">Content</div>
                  <div className="flex flex-col items-start gap-1">
                    {(content?.headings ?? []).map((heading) => (
                      <a
                        key={heading.id}
                        href={`#${heading.id}`}
                        className={tocItemClassName(heading.level)}
                        data-level={heading.level}
                      >
                        {heading.text}
                      </a>
                    ))}
                  </div>
                </aside>

                <article
                  className="text-rich-text w-richtext blog-richtext w-full min-w-0"
                  dangerouslySetInnerHTML={{ __html: content?.html ?? "" }}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-[6.25rem] max-[767px]:py-16">
          <div className="px-[var(--site-margin)]">
            <div className="mx-auto w-full max-w-[80rem]">
              <div className="flex flex-col items-start">
                <span className="flex w-fit justify-start rounded-full border-[0.09rem] border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#ffffff_40%,transparent)] p-[0.38rem] font-semibold text-pink-dark">
                  <span className="flex items-center gap-1 rounded-[inherit] border-[inherit] bg-white px-[0.88rem] py-2 text-[0.88rem] font-semibold leading-none">
                    <Sparkles className="h-5 w-5 shrink-0" />
                    Blogs
                  </span>
                </span>
                <div className="w-full pt-6 max-[767px]:pt-5" />
                <h2 className="m-0 font-display text-[clamp(2rem,1.5714rem+2.1429vw,3.5rem)] font-medium leading-[1.15] tracking-[-0.02em] text-[#1f0a18]">
                  Read few more <br />
                  <span className="text-pink-dark">Stories by us</span>
                </h2>
                <div className="w-full pt-10 max-[767px]:pt-6" />
                <div className="grid w-full grid-cols-[1fr_1fr_1fr] gap-4 max-[991px]:grid-cols-[repeat(2,minmax(0,1fr))] max-[767px]:grid-cols-1">
                  {relatedPosts.map((relatedPost) => (
                    <BlogCard key={relatedPost.slug} post={relatedPost} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <BlogNewsletterSignupSection />
      </main>

      <Footer />
    </div>
  );
}

export default BlogPostPage;
